import {ROOT_TAG} from "../../globals";
import {unsafeStatic} from "lit/static-html.js";
import {customElement} from "lit/decorators.js";
import {html, LitElement, nothing} from "lit";
import {consume} from "@lit/context";
import {BridgeBarConfigContext} from "../../config";
import type {BridgeBarConfig, Deployment, Theme} from "../../config";
import {BridgeBarDataContext} from "../../backend";
import type {BridgeBarDataContextValue} from "../../backend/BridgeBarData";
import {features, LDClientContext} from "../../launchdarkly";
import type {LDClientContextValue} from "../../launchdarkly";
import {getBbEnv} from "../../lib/bbEnv";

const tagName = `${ROOT_TAG}-support-menu`;

export const SupportMenu = unsafeStatic(tagName);

const SUPPORT_MENU_TAG = "enterprise-product-experience-support-menu";

const SUPPORT_MENU_SCRIPT_FILE = "support-menu.esm.js";

const SUPPORT_MENU_CDN_HOST: Record<Deployment, string> = {
  dz: "bridge-support-menu.dangerzone.coxautoservices.com",
  live: "bridge-support-menu.coxautoservices.com"
};

const SUPPORT_MENU_THEME: Record<Theme, string> = {
  light: "Interstate-Light",
  dark: "Interstate-Dark"
};

@customElement(tagName)
export class SupportMenuElement extends LitElement {
  @consume({context: BridgeBarConfigContext, subscribe: true})
  private config: BridgeBarConfig | undefined;

  @consume({context: BridgeBarDataContext, subscribe: true})
  private data: BridgeBarDataContextValue | undefined;

  @consume({context: LDClientContext, subscribe: true})
  private ldContextValue: LDClientContextValue | undefined;

  private supportMenuElement: HTMLElement | null = null;

  private ensureScriptLoaded(deployment: Deployment): void {
    const host = SUPPORT_MENU_CDN_HOST[deployment];
    if (!host) return;
    const src = `https://${host}/${SUPPORT_MENU_SCRIPT_FILE}`;
    if (document.querySelector(`script[src="${src}"]`)) return;
    const script = document.createElement("script");
    script.type = "module";
    script.src = src;
    document.head.appendChild(script);
  }

  private createSupportMenuEl(
    platformId: string,
    bbEnv: string,
    attributes: Record<string, unknown>
  ): HTMLElement | null {
    try {
      const showLms = attributes?.showlms ? "true" : "false";
      const showPendo = attributes?.showpendo ? "true" : "false";
      const el = document.createElement(SUPPORT_MENU_TAG);
      el.setAttribute("bb_session", this.config.sessionId);
      el.setAttribute("bb_user", platformId);
      el.setAttribute("showlms", showLms);
      el.setAttribute("showpendo", showPendo);
      el.setAttribute("bb_theme", SUPPORT_MENU_THEME[this.config.theme]);
      el.setAttribute("bb_env", bbEnv);
      return el;
    } catch (error) {
      console.error(
        `[SupportMenuElement] Failed to create ${SUPPORT_MENU_TAG}:`,
        error
      );
      return null;
    }
  }
  override updated(): void {
    if (this.config) {
      this.ensureScriptLoaded(this.config.deployment);
    }
  }

  override render() {
    if (!this.config) return nothing;
    if (this.data?.status !== "complete") return nothing;

    const platformId = this.data.data.id;
    if (!platformId) return nothing;

    const bbEnv = getBbEnv(this.config.deployment, this.config.partition);
    if (!bbEnv) return nothing;

    // Cache the element so re-renders reuse the same instance
    const defaultFlagValue = {enabled: false, attributes: {}};
    const enableSupportMenuFlag =
      this.ldContextValue?.ldClient?.variation(
        features.dev.enableSupportMenu,
        defaultFlagValue
      ) ?? defaultFlagValue;
    if (!enableSupportMenuFlag.enabled) return nothing;
    if (!this.supportMenuElement) {
      this.supportMenuElement = this.createSupportMenuEl(
        platformId,
        bbEnv,
        enableSupportMenuFlag.attributes
      );
    }
    if (!this.supportMenuElement) return nothing;
    return html`${this.supportMenuElement}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: SupportMenuElement;
  }
}
